import { useEffect, useMemo, useRef, useState } from 'react';

import { Button } from '@/components/ui/button';
import { useCurrentUser } from '@/features/identity';

import {
  BUCKET_DOT,
  BUCKET_FILTER,
  COVERAGE_BUCKETS,
  coverage as coverageOf,
} from '../commitCoverage';
import {
  readSnapshot,
  sinceVisit,
  snapshotOf,
  writeSnapshot,
} from '../coverageVisit';
import { useForgeApps } from '../hooks/useForgeApps';
import { usePeopleDirectory } from '../hooks/useLinkedAccounts';
import { hostsInUse, type PeopleFilter } from '../linkedAccounts';
import {
  COMMIT_FIXES_COPY as FIXES,
  COMMIT_SUMMARY_COPY as WORDS,
} from '../linkedAccountsCopy';
import { ConsoleAnswer } from './ConsoleHeader';
import { CoverageRing } from './CoverageRing';

/** Where the people list starts, for a legend entry to bring into view. */
export const PEOPLE_HEADING = 'commit-identity-people';

/**
 * Commit identity's answer (spec 164 US1, on the console header since spec
 * 168): how many people push as themselves, the ring, and a legend whose
 * entries open the people they count. Under it, what changed since this admin
 * was last here (US3). The legend is plain words with dots, not pills: the
 * filter chips below are the controls, and two rows of pills read as two
 * sets of filters.
 */
export function CoverageSummary({
  onShow,
  onSetUp,
}: {
  onShow: (show: PeopleFilter) => void;
  /** Open sign-in setup (spec 169): the zero state's next step. */
  onSetUp: () => void;
}) {
  const directory = usePeopleDirectory();
  const apps = useForgeApps();
  const [now] = useState(() => new Date());
  const data = directory.data;
  const inUse = useMemo(() => hostsInUse(data?.hosts ?? []), [data?.hosts]);
  const counts = useMemo(
    () => coverageOf(data?.items ?? [], inUse, now),
    [data?.items, inUse, now],
  );

  if (!data) return null;
  if (data.items.length === 0 || inUse.length === 0) {
    return (
      <p className="text-muted-foreground text-sm">
        {data.items.length === 0 ? WORDS.noPeople : WORDS.noHosts}
      </p>
    );
  }

  // Only offered where it would help: sign-in is possible and a host in use
  // still lacks it.
  const appList = apps.data;
  const signInMissing =
    appList?.available === true &&
    inUse.some(
      (host) =>
        !appList.items.some((app) => app.host === host && app.configured),
    );

  const open = (filter: PeopleFilter) => {
    onShow(filter);
    const heading = document.getElementById(PEOPLE_HEADING);
    heading?.scrollIntoView?.({ block: 'start', behavior: 'smooth' });
  };

  return (
    <ConsoleAnswer
      visual={<CoverageRing coverage={counts} size={72} />}
      figure={WORDS.answer(counts.themselves, counts.total)}
      words={WORDS.answerWords}
      detail={
        <div className="flex flex-col gap-2">
          <ul
            aria-label={WORDS.legendLabel}
            // Two even columns on a phone, not a ragged wrap (spec 280).
            className="flex flex-wrap gap-x-4 gap-y-1 max-md:grid max-md:grid-cols-2"
          >
            {COVERAGE_BUCKETS.map((bucket) => {
              const filter = BUCKET_FILTER[bucket];
              const count = counts[bucket];
              const body = (
                <>
                  <span
                    aria-hidden="true"
                    className={`size-2 shrink-0 rounded-full ${BUCKET_DOT[bucket]}`}
                  />
                  {WORDS.buckets[bucket]}
                  <span className="text-foreground font-medium tabular-nums">
                    {count}
                  </span>
                </>
              );
              const shape =
                'inline-flex min-h-8 items-center gap-1.5 text-sm whitespace-nowrap pointer-coarse:min-h-11';
              return (
                <li key={bucket}>
                  {filter && count > 0 ? (
                    <button
                      type="button"
                      aria-label={`${WORDS.buckets[bucket]}: ${String(count)}. ${WORDS.showBucket(WORDS.buckets[bucket])}`}
                      onClick={() => open(filter)}
                      className={`${shape} focus-visible:ring-ring text-muted-foreground hover:text-foreground rounded-sm underline-offset-4 hover:underline focus-visible:ring-3 focus-visible:outline-none`}
                    >
                      {body}
                    </button>
                  ) : (
                    <span className={`${shape} text-muted-foreground`}>
                      {body}
                    </span>
                  )}
                </li>
              );
            })}
          </ul>
          {counts.themselves === 0 ? (
            // At zero the ring is a grey loop that says nothing, so the words
            // say it, with the step that moves the number (spec 169).
            <div className="flex flex-wrap items-center gap-x-3 gap-y-2">
              <p className="text-sm">
                <span className="text-foreground font-medium">
                  {FIXES.nobodyYet}
                </span>{' '}
                {FIXES.nobodyNext}
              </p>
              {signInMissing ? (
                <Button
                  variant="outline"
                  size="sm"
                  className="max-md:w-full"
                  onClick={onSetUp}
                >
                  {FIXES.setUpSignIn}
                </Button>
              ) : null}
            </div>
          ) : null}
          <SinceLastVisit />
        </div>
      }
    />
  );
}

/**
 * One line, when something changed while the admin was away (US3). The last
 * visit is read once, when the page opens; leaving the page keeps how it
 * stands now for next time.
 */
function SinceLastVisit() {
  const userId = useCurrentUser().data?.id;
  const directory = usePeopleDirectory();
  const data = directory.data;
  const [opened] = useState(() => Date.now());
  const previous = useMemo(
    () => (userId ? readSnapshot(userId) : null),
    [userId],
  );
  const current = useMemo(
    () =>
      data
        ? snapshotOf(data.items, hostsInUse(data.hosts), new Date(opened))
        : null,
    [data, opened],
  );
  // Read between renders when the page is left.
  const latest = useRef(current);
  useEffect(() => {
    latest.current = current;
  }, [current]);

  useEffect(() => {
    if (!userId) return;
    const leave = () => {
      if (latest.current) {
        writeSnapshot(userId, {
          ...latest.current,
          at: new Date().toISOString(),
        });
      }
    };
    const onVisibility = () => {
      if (document.visibilityState === 'hidden') leave();
    };
    document.addEventListener('visibilitychange', onVisibility);
    window.addEventListener('pagehide', leave);
    return () => {
      document.removeEventListener('visibilitychange', onVisibility);
      window.removeEventListener('pagehide', leave);
      leave();
    };
  }, [userId]);

  const since = current ? sinceVisit(previous, current, opened) : null;
  if (!since) return null;
  return (
    <p className="text-muted-foreground text-sm">
      <span className="text-console-accent font-medium">{WORDS.sinceLead}</span>{' '}
      {WORDS.since(since.joined, since.broke)}
    </p>
  );
}
